// ContentSection.tsx — Settings → Content Preferences.
//
// SPEECH-SAFETY INVARIANT: nothing in this section can change what anyone
// else can post or see, or how the Council treats a report. The platform's
// five-and-only-five moderation categories are handled by the Council;
// keyword filters are README-deferred. Every control here is a viewer-side
// choice that affects only the signed-in user's own view:
//
//   Default feed tab → defaultFeedTab pref, consumed by HomePage's initial tab
//                      state. 'Following' is the platform default — the
//                      anti-virality thesis makes algorithmic tabs opt-in.
//   Feed density     → feedDensity pref, consumed by PostCard's container
//                      spacing. Same posts, tighter pixels.
//   Muted accounts   → manage list for the mute relationship the ··· menu
//                      already writes; unmuting here keeps muteStore in sync.
//   Hidden posts     → manage list for "Not interested"; unhiding restores a
//                      post to this viewer's own feed only.
import { DiscussionSettings } from '@/features/discussions/DiscussionSettings';
import { FeedOrderControl } from '@/features/home/FeedOrderControl';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Panel, Avatar, Switch, useToast } from '@/ui';
import {
  usePreferences,
  useMutedAccountsList,
  useHiddenPostsList,
  useSession,
  useTopics,
} from '@/hooks';
import { InterestPicker } from '@/features/onboarding/InterestPicker';
import type { DefaultFeedTabKey, FeedDensityKey } from '@/core/models/preferences';
import type { MutedAccount } from '@/core/models/account';
import type { Post } from '@/core/models/post';

/**
 * Focus a specific row's action button by key after the list re-renders, or
 * fall back to `fallback` (typically the card heading) when that key no
 * longer has a row — e.g. the list just emptied. Deferred to the next frame:
 * the caller's row has usually just been removed from state, so the button
 * we want to focus (or its replacement) isn't in the DOM yet this tick.
 */
function focusRowOrFallback(
  refs: Map<string, HTMLButtonElement>,
  key: string | null,
  fallback: HTMLElement | null,
) {
  requestAnimationFrame(() => {
    const btn = key ? refs.get(key) : null;
    (btn ?? fallback)?.focus();
  });
}

/**
 * Re-focus a row's own button after a failed request. Deferred for the same
 * reason as focusRowOrFallback above: the button is still `disabled` (and a
 * disabled element cannot receive focus) at the moment the failure resolves
 * — `pending` only clears on the NEXT render, so calling .focus() eagerly
 * here focused nothing and the browser dropped focus to <body>.
 */
function refocusRow(refs: Map<string, HTMLButtonElement>, key: string) {
  requestAnimationFrame(() => {
    refs.get(key)?.focus();
  });
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const FooterNote = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SegmentGroup = styled.div`
  display: flex;
  gap: 2px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  width: fit-content;
`;

const Segment = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  background: ${({ $active, theme }) => ($active ? theme.color.glassBorder : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $active, theme }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  transition:
    background 0.12s,
    color 0.12s;
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: -2px;
  }
`;

const ManageList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const ManageRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const ManageRowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ManageName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ManageHandle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Snippet = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  flex: none;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;
  transition: background 0.12s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const StateText = styled.p`
  margin: 0;
  padding: 4px 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const RetryButton = styled.button`
  padding: 4px 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const DeletedNote = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;

const TAB_OPTIONS: { key: DefaultFeedTabKey; label: string }[] = [
  { key: 'following', label: 'Following' },
  { key: 'for-you', label: 'For You' },
  { key: 'trending', label: 'Trending' },
  { key: 'discover', label: 'Discover' },
];

const DENSITY_OPTIONS: { key: FeedDensityKey; label: string }[] = [
  { key: 'comfortable', label: 'Comfortable' },
  { key: 'compact', label: 'Compact' },
];

/**
 * Muted accounts — manage list. Unmuting reuses usersService.toggleMute (via
 * useMutedAccountsList), the same call the ··· menu makes, so muteStore stays
 * the single source of truth and the ··· menu is immediately correct without
 * a reload — no local mute bookkeeping happens here.
 */
function MutedAccountsCard() {
  const { accounts, loading, error, pending, unmute, refresh } = useMutedAccountsList();
  const toast = useToast();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  function setButtonRef(handle: string) {
    return (el: HTMLButtonElement | null) => {
      if (el) buttonRefs.current.set(handle, el);
      else buttonRefs.current.delete(handle);
    };
  }

  async function handleUnmute(account: MutedAccount) {
    // Compute the neighbor BEFORE the row disappears — accounts here is the
    // pre-removal array from this render.
    const idx = accounts.findIndex((a) => a.handle === account.handle);
    const neighbor = accounts[idx + 1]?.handle ?? accounts[idx - 1]?.handle ?? null;

    const outcome = await unmute(account.handle);
    if (outcome === 'busy') return; // a request for this row is already in flight
    if (outcome === 'unmuted') {
      toast(`Unmuted ${account.handle}`);
      // The row (and its own button) is gone once state updates — move focus
      // to the next row's button (previous if this was last), or the card
      // heading if the list just emptied. Never let it fall to <body>.
      focusRowOrFallback(buttonRefs.current, neighbor, titleRef.current);
    } else if (outcome === 'stale') {
      toast('That account was already changed elsewhere. Refreshed the list');
    } else {
      toast('Something went wrong');
      // Failure path: the button is about to lose `disabled`, but nothing
      // else changed — keep focus right where the user's attention already is.
      refocusRow(buttonRefs.current, account.handle);
    }
  }

  return (
    <Card>
      <CardTitle ref={titleRef} tabIndex={-1}>
        Muted accounts
      </CardTitle>
      <RowHint>
        Posts from muted accounts stay out of your feed. Unmuting here has the same effect as
        unmuting from a post&apos;s three-dot menu. The change reaches your feed on its next
        refresh.
      </RowHint>

      {loading && (
        <StateText role="status" aria-live="polite">
          Loading…
        </StateText>
      )}
      {!loading && error && (
        <ErrorRow role="status" aria-live="polite">
          <StateText as="span">Could not load your muted accounts.</StateText>
          <RetryButton type="button" onClick={refresh}>
            Try again
          </RetryButton>
        </ErrorRow>
      )}
      {!loading && !error && accounts.length === 0 && (
        <StateText role="status" aria-live="polite">
          You haven&apos;t muted anyone.
        </StateText>
      )}
      {!loading && !error && accounts.length > 0 && (
        <ManageList aria-label="Muted accounts">
          {accounts.map((account) => (
            <ManageRow key={account.handle}>
              <Avatar name={account.name} avatarKey={account.av} size={28} />
              <ManageRowText>
                <ManageName>{account.name}</ManageName>
                <ManageHandle>{account.handle}</ManageHandle>
              </ManageRowText>
              <ActionButton
                ref={setButtonRef(account.handle)}
                type="button"
                disabled={pending.has(account.handle)}
                onClick={() => void handleUnmute(account)}
              >
                Unmute {account.handle}
              </ActionButton>
            </ManageRow>
          ))}
        </ManageList>
      )}
    </Card>
  );
}

/**
 * Hidden posts — manage list. Unhiding reuses postsService.unhidePost (via
 * useHiddenPostsList) with `restoreToFeed: false` — a post unhidden here is
 * NOT injected into the home feed store. It may be days old, and dropping it
 * straight to the top of the feed (or anywhere in it) would be wrong; it
 * simply reappears naturally the next time the feed is refetched.
 */
function HiddenPostsCard() {
  const { posts, loading, error, pending, unhide, refresh } = useHiddenPostsList();
  const toast = useToast();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  function setButtonRef(id: string) {
    return (el: HTMLButtonElement | null) => {
      if (el) buttonRefs.current.set(id, el);
      else buttonRefs.current.delete(id);
    };
  }

  async function handleUnhide(post: Post) {
    const idx = posts.findIndex((p) => p.id === post.id);
    const neighbor = posts[idx + 1]?.id ?? posts[idx - 1]?.id ?? null;

    const outcome = await unhide(post);
    if (outcome === 'busy') return; // a request for this row is already in flight
    if (outcome === 'unhidden') {
      toast("Post unhidden. It'll show in your feed again");
      focusRowOrFallback(buttonRefs.current, neighbor, titleRef.current);
    } else if (outcome === 'stale') {
      toast('That post was already changed elsewhere. Refreshed the list');
    } else {
      toast('Something went wrong');
      refocusRow(buttonRefs.current, post.id);
    }
  }

  return (
    <Card>
      <CardTitle ref={titleRef} tabIndex={-1}>
        Hidden posts
      </CardTitle>
      <RowHint>
        Posts you marked &quot;Not interested&quot; stay out of your feed. Unhiding restores a post
        to your own view only. It was never removed from anyone else&apos;s.
      </RowHint>

      {loading && (
        <StateText role="status" aria-live="polite">
          Loading…
        </StateText>
      )}
      {!loading && error && (
        <ErrorRow role="status" aria-live="polite">
          <StateText as="span">Could not load your hidden posts.</StateText>
          <RetryButton type="button" onClick={refresh}>
            Try again
          </RetryButton>
        </ErrorRow>
      )}
      {!loading && !error && posts.length === 0 && (
        <StateText role="status" aria-live="polite">
          No hidden posts.
        </StateText>
      )}
      {!loading && !error && posts.length > 0 && (
        <ManageList aria-label="Hidden posts">
          {posts.map((post) => (
            <ManageRow key={post.id}>
              <Avatar name={post.name} avatarKey={post.av} size={28} />
              <ManageRowText>
                <ManageName>{post.name}</ManageName>
                <Snippet>{post.body}</Snippet>
                {post.deleted && <DeletedNote>Deleted by author</DeletedNote>}
              </ManageRowText>
              <ActionButton
                ref={setButtonRef(post.id)}
                type="button"
                disabled={pending.has(post.id)}
                aria-label={`Unhide post from ${post.handle}`}
                onClick={() => void handleUnhide(post)}
              >
                Unhide
              </ActionButton>
            </ManageRow>
          ))}
        </ManageList>
      )}
    </Card>
  );
}

/**
 * Interests — the POSITIVE topic list from first-run onboarding (1.24.00 B1),
 * kept editable here so the choice is never one-shot. Same shared picker,
 * same write (`updateProfile({ interests })`), optimistic with rollback so a
 * pill flips on the click and flips back if the server says no. Distinct from
 * the For You topic MENU on Home, which edits `feedTopics` — the opt-OUT list.
 */
function InterestsRow() {
  const { currentUser, updateProfile } = useSession();
  const { topics, loading } = useTopics();
  const toast = useToast();
  const server = currentUser?.interests ?? [];
  const [override, setOverride] = useState<string[] | null>(null);
  const value = override ?? server;

  function handleChange(next: string[]) {
    setOverride(next);
    void updateProfile({ interests: next }).then((ok) => {
      // Success keeps the override: with two PATCHes in flight only the newest
      // response re-caches the session (authService's stale guard), so
      // clearing on the OLDER success would flash the pills off for a beat.
      if (!ok) {
        toast('Something went wrong');
        setOverride(null); // roll back to the server's list
      }
    });
  }

  return (
    <Row>
      <RowText>
        <RowLabel>Interests</RowLabel>
        <RowHint>
          Topics you&apos;re into. Boosts them in For You and shapes who we suggest. Never hides
          anything.
        </RowHint>
      </RowText>
      {loading ? (
        <StateText role="status">Loading topics…</StateText>
      ) : (
        <InterestPicker topics={topics} value={value} onChange={handleChange} label="Interests" />
      )}
    </Row>
  );
}

export function ContentSection() {
  const { prefs, setPreference } = usePreferences();
  const { currentUser, updateProfile } = useSession();
  const toast = useToast();
  const showFeedReasons = currentUser?.showFeedReasons ?? false;

  function handleToggleShowFeedReasons(next: boolean) {
    void updateProfile({ showFeedReasons: next }).then((ok) => {
      if (!ok) toast('Something went wrong');
    });
  }

  return (
    <Stack>
      <DiscussionSettings />
      <Card>
        <CardTitle>Your feed</CardTitle>
        <FeedOrderControl />
        <Row>
          <RowText>
            <RowLabel>Remember my place in the feed</RowLabel>
            <RowHint>
              Return to your last tab and reading position in this browser session. Off by default.
            </RowHint>
          </RowText>
          <Switch
            checked={prefs.rememberFeedPosition}
            onChange={(next) => void setPreference('rememberFeedPosition', next)}
            label="Remember my place in the feed"
          />
        </Row>

        <Row>
          <RowText>
            <RowLabel>Default feed tab</RowLabel>
            <RowHint>
              Where Home lands. Following is chronological, the platform default; the algorithmic
              tabs are always one tap away.
            </RowHint>
          </RowText>
          <SegmentGroup role="group" aria-label="Default feed tab">
            {TAB_OPTIONS.map(({ key, label }) => (
              <Segment
                key={key}
                type="button"
                $active={prefs.defaultFeedTab === key}
                aria-pressed={prefs.defaultFeedTab === key}
                onClick={() => void setPreference('defaultFeedTab', key)}
              >
                {label}
              </Segment>
            ))}
          </SegmentGroup>
        </Row>

        <Row>
          <RowText>
            <RowLabel>Feed density</RowLabel>
            <RowHint>Compact tightens card spacing so more posts fit on screen.</RowHint>
          </RowText>
          <SegmentGroup role="group" aria-label="Feed density">
            {DENSITY_OPTIONS.map(({ key, label }) => (
              <Segment
                key={key}
                type="button"
                $active={prefs.feedDensity === key}
                aria-pressed={prefs.feedDensity === key}
                onClick={() => void setPreference('feedDensity', key)}
              >
                {label}
              </Segment>
            ))}
          </SegmentGroup>
        </Row>

        <InterestsRow />

        <Row>
          <RowText>
            <RowLabel>Show why on every post</RowLabel>
            <RowHint>
              When on, every For You post shows a short reason line (e.g. &quot;You follow
              @handle&quot;). It&apos;s always available from a post&apos;s three-dot menu either
              way.
            </RowHint>
          </RowText>
          <Switch
            checked={showFeedReasons}
            onChange={handleToggleShowFeedReasons}
            label="Show why on every post"
          />
        </Row>

        <FooterNote>
          These preferences change how your feed is laid out, never what&apos;s in it. TruXSocial
          doesn&apos;t offer keyword filters. Muting and hiding below change only your own view
          (never what anyone else can post or see), and moderation itself still belongs to the
          Council.
        </FooterNote>
      </Card>

      <MutedAccountsCard />
      <HiddenPostsCard />
    </Stack>
  );
}
